import { useState } from "react";
import { X, StickyNote, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

/**
 * Small floating popover that appears near selected text in the reader.
 * Props: { selection: {text, rect}, bookId, page, onClose, onSaved }
 */
export default function HighlightPopover({ selection, bookId, page, onClose, onSaved }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  if (!selection) return null;

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/notes", {
        body: body.trim() || selection.text.slice(0, 120),
        book_id: bookId,
        page,
        excerpt: selection.text.slice(0, 800),
      });
      toast.success("saved to notepad.");
      onSaved && onSaved(r.data);
      onClose();
    } catch (e) {
      const msg = e?.response?.status === 401 ? "sign in to save notes" : "could not save note";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // Position: try to place above the selection midpoint; clamp within viewport
  const top = Math.max(70, selection.rect.top + window.scrollY - 60);
  const left = Math.min(
    window.innerWidth - 360,
    Math.max(16, selection.rect.left + selection.rect.width / 2 - 180)
  );

  return (
    <div
      data-testid="highlight-popover"
      className="absolute z-40"
      style={{ top, left }}
    >
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="highlight-save-btn"
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full font-ui text-xs tracking-caps paper-shadow hover:opacity-90 transition-opacity"
        >
          <StickyNote className="w-3.5 h-3.5" /> save to notepad
        </button>
      ) : (
        <div className="bg-card border border-border rounded-lg paper-shadow w-[360px] p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-ui text-xs tracking-caps text-primary">new note</div>
            <button onClick={onClose} className="p-1 hover:bg-secondary rounded-full">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="font-reader italic text-sm text-muted-foreground border-l-2 border-primary/40 pl-3 mb-3 line-clamp-3">
            &ldquo;{selection.text}&rdquo;
          </div>
          <Textarea
            placeholder="add a thought…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid="highlight-note-input"
            className="min-h-[90px] font-reader text-sm"
            autoFocus
          />
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} className="font-ui text-xs">cancel</Button>
            <Button
              onClick={save}
              disabled={busy}
              data-testid="highlight-confirm-btn"
              className="font-ui text-xs rounded-full"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
